import React, { useState, useRef, useEffect } from "react";
import { Send, Smile, Paperclip, Mic, Search, Users, Phone, Video, Info, Bot } from "lucide-react";

export default function Chat() {
  const [users] = useState([
    { id: 1, name: "Alice", color: "bg-pink-500", status: "online", lastSeen: "2 min ago" },
    { id: 2, name: "Bob", color: "bg-blue-500", status: "offline", lastSeen: "1 hour ago" },
    { id: 3, name: "Charlie", color: "bg-green-500", status: "online", lastSeen: "5 min ago" },
    { id: 4, name: "You", color: "bg-purple-500", status: "online", lastSeen: "Just now" },
    { id: 5, name: "AI Assistant", color: "bg-cyan-500", status: "online", lastSeen: "Always", isAI: true },
  ]);

  const [messages, setMessages] = useState([
    {
      id: 1,
      user: "AI Assistant",
      color: "bg-cyan-500",
      text: "Hello! I'm your AI assistant. How can I help you today? 🤖",
      time: "09:30",
      type: "text",
      isAI: true
    }
  ]);

  const [activeUser, setActiveUser] = useState(users[4]);
  const [newMessage, setNewMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const generateAIResponse = (userMessage) => {
    const lowerMsg = userMessage.toLowerCase();
    let response = "";
    
    if (lowerMsg.includes("song") || lowerMsg.includes("music")) {
      response = "I can help you find songs! Try searching in the Search tab. What genre or artist do you like? 🎵";
    } else if (lowerMsg.includes("playlist")) {
      response = "You can create playlists in the Library section. Go to Create Playlist to make your own! 📋";
    } else if (lowerMsg.includes("premium") || lowerMsg.includes("subscription")) {
      response = "Check the Premium page for subscription plans. We have monthly and yearly options! 💎";
    } else if (lowerMsg.includes("hello") || lowerMsg.includes("hi") || lowerMsg.includes("hey")) {
      response = "Hey there! 👋 How can I assist you today?";
    } else if (lowerMsg.includes("help")) {
      response = "I'm here to help! You can ask me about:\n• Finding songs\n• Creating playlists\n• Premium plans\n• App features\n\nWhat would you like to know?";
    } else if (lowerMsg.includes("download")) {
      response = "You can download songs for offline listening with Premium! Visit the Premium page to learn more. 📥";
    } else if (lowerMsg.includes("artist") || lowerMsg.includes("singer")) {
      response = "Check the Artists page to browse all singers. You can also search for specific artists in the Search tab! 🎤";
    } else {
      const replies = [
        "That's interesting! Tell me more! 🤔",
        "I see! How can I help you with that? 💡",
        "Got it! Is there anything else you'd like to know?",
        "Great question! Let me think... 💭",
        "I'm here to help! Try asking about songs, playlists, or premium plans.",
        "Interesting! Would you like me to suggest some music? 🎶"
      ];
      response = replies[Math.floor(Math.random() * replies.length)];
    }
    
    return response;
  };

  const handleSend = () => {
    if (!newMessage.trim()) return;

    const now = new Date();
    const time = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    const isUserMessage = activeUser.name === "You" || !activeUser.isAI;

    setMessages([
      ...messages,
      {
        id: messages.length + 1,
        user: isUserMessage ? "You" : activeUser.name,
        color: isUserMessage ? "bg-purple-500" : activeUser.color,
        text: newMessage,
        time,
        type: "text",
        isAI: activeUser.isAI
      },
    ]);

    setNewMessage("");
    
    if (activeUser.isAI || isUserMessage) {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        const aiResponse = generateAIResponse(newMessage);
        setMessages(prev => [
          ...prev,
          {
            id: prev.length + 1,
            user: "AI Assistant",
            color: "bg-cyan-500",
            text: aiResponse,
            time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
            type: "text",
            isAI: true
          }
        ]);
      }, 1500);
    } else if (activeUser.name !== "You") {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        const autoReplies = [
          "That's interesting! 🤔",
          "I agree with you! 👍",
          "Let me think about that... 💭",
          "Great point! 👏",
          "Haha, that's funny 😄"
        ];
        const randomReply = autoReplies[Math.floor(Math.random() * autoReplies.length)];
        
        setMessages(prev => [
          ...prev,
          {
            id: prev.length + 1,
            user: "Alice",
            color: "bg-pink-500",
            text: randomReply,
            time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
            type: "text"
          }
        ]);
      }, 2000);
    }
  };

  const quickReactions = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

  return (
    <div className="flex h-screen w-full bg-gradient-to-br from-indigo-400 via-purple-400 to-pink-400">
      {/* Sidebar */}
      <div className="w-80 bg-white/20 backdrop-blur-lg border-r border-white/30 hidden lg:flex flex-col">
        {/* Sidebar Header */}
        <div className="p-6 border-b border-white/20">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-white">Messages</h1>
            <button className="p-2 hover:bg-white/20 rounded-xl transition-all duration-300">
              <Users className="w-6 h-6 text-white" />
            </button>
          </div>
          
          {/* Search Bar */}
          <div className="relative mt-4">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70" />
            <input
              type="text"
              placeholder="Search messages..."
              className="w-full pl-10 pr-4 py-3 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50 backdrop-blur-sm"
            />
          </div>
        </div>

        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {users.map((user) => (
            <div
              key={user.id}
              className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-300 hover:bg-white/20 ${
                activeUser.id === user.id ? "bg-white/30 backdrop-blur-sm" : ""
              }`}
              onClick={() => setActiveUser(user)}
            >
              <div className="relative">
                <div className={`w-12 h-12 rounded-full ${user.color} flex items-center justify-center text-white font-bold shadow-lg ${user.isAI ? 'border-2 border-cyan-300' : ''}`}>
                  {user.isAI ? <Bot className="w-6 h-6" /> : user.name[0]}
                </div>
                <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${
                  user.status === "online" ? "bg-green-400" : "bg-gray-400"
                }`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-white truncate">{user.name}</h3>
                  <span className="text-xs text-white/70">{user.lastSeen}</span>
                </div>
                <p className="text-sm text-white/80 truncate">
                  {user.status === "online" ? "Online" : "Offline"}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Chat Header */}
        <div className="bg-white/20 backdrop-blur-lg border-b border-white/30 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className={`w-12 h-12 rounded-full ${activeUser.color} flex items-center justify-center text-white font-bold shadow-lg ${activeUser.isAI ? 'border-2 border-cyan-300' : ''}`}>
                  {activeUser.isAI ? <Bot className="w-6 h-6" /> : activeUser.name[0]}
                </div>
                <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${
                  activeUser.status === "online" ? "bg-green-400" : "bg-gray-400"
                }`} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  {activeUser.isAI && <Bot className="inline w-5 h-5 mr-1" />}
                  {activeUser.name}
                </h2>
                <p className="text-white/80 text-sm">
                  {activeUser.isAI ? "AI Assistant 🤖" : activeUser.status === "online" ? "Online" : `Last seen ${activeUser.lastSeen}`}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button className="p-3 hover:bg-white/20 rounded-xl transition-all duration-300 group">
                <Phone className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
              </button>
              <button className="p-3 hover:bg-white/20 rounded-xl transition-all duration-300 group">
                <Video className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
              </button>
              <button className="p-3 hover:bg-white/20 rounded-xl transition-all duration-300 group">
                <Info className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
              </button>
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gradient-to-b from-white/10 to-white/5 backdrop-blur-sm">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-24 h-24 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                <span className="text-4xl">💬</span>
              </div>
              <p className="text-white/80 text-lg">No messages yet. Start the conversation! 👋</p>
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex items-start gap-3 group ${
                  msg.user === activeUser.name ? "justify-end" : "justify-start"
                }`}
              >
                {msg.user !== activeUser.name && (
                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 rounded-full ${msg.color} flex items-center justify-center text-white font-bold text-sm shadow-md ${msg.isAI ? 'border-2 border-cyan-300' : ''}`}>
                      {msg.isAI ? <Bot className="w-5 h-5" /> : msg.user[0]}
                    </div>
                  </div>
                )}
                
                <div className="flex flex-col gap-1 max-w-[70%]">
                  {msg.user !== activeUser.name && (
                    <span className="text-white/70 text-sm font-medium px-2">
                      {msg.isAI && <Bot className="inline w-3 h-3 mr-1" />}
                      {msg.user}
                    </span>
                  )}
                  <div
                    className={`relative px-4 py-3 rounded-2xl shadow-lg backdrop-blur-sm ${
                      msg.user === activeUser.name 
                        ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-br-md" 
                        : "bg-white/90 text-gray-800 rounded-bl-md"
                    }`}
                  >
                    <p className="break-words text-sm sm:text-base leading-relaxed">{msg.text}</p>
                    <span className={`text-xs block mt-2 ${
                      msg.user === activeUser.name ? "text-white/70" : "text-gray-500"
                    }`}>
                      {msg.time}
                    </span>
                    
                    {/* Quick Reactions */}
                    <div className="absolute -bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="flex gap-1 bg-white/90 backdrop-blur-sm rounded-full p-1 shadow-lg border border-white/20">
                        {quickReactions.slice(0, 3).map((reaction, index) => (
                          <button
                            key={index}
                            className="w-6 h-6 hover:scale-125 transition-transform duration-200 text-xs"
                            onClick={() => {
                              // Handle reaction
                              console.log(`Reacted with ${reaction} to message ${msg.id}`);
                            }}
                          >
                            {reaction}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                
                {msg.user === activeUser.name && (
                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 rounded-full ${msg.color} flex items-center justify-center text-white font-bold text-sm shadow-md`}>
                      {msg.user[0]}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
          
          {/* Typing Indicator */}
          {isTyping && (
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-cyan-500 flex items-center justify-center text-white font-bold text-sm shadow-md">
                <Bot className="w-5 h-5" />
              </div>
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl rounded-bl-md px-4 py-3 shadow-lg">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="bg-white/20 backdrop-blur-lg border-t border-white/30 p-4">
          {/* User Selector for Mobile */}
          <div className="flex gap-2 mb-4 overflow-x-auto lg:hidden">
            {users.map((user) => (
              <button
                key={user.id}
                onClick={() => setActiveUser(user)}
                className={`px-4 py-2 rounded-full text-sm font-semibold shadow-lg transition-all duration-300 backdrop-blur-sm ${
                  activeUser.id === user.id 
                    ? `${user.color} text-white shadow-md transform scale-105` 
                    : "bg-white/80 text-gray-700 hover:bg-white"
                }`}
              >
                {user.name}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            {/* Attachment Buttons */}
            <div className="flex gap-1">
              <button className="p-3 hover:bg-white/20 rounded-xl transition-all duration-300 group">
                <Paperclip className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
              </button>
              <button className="p-3 hover:bg-white/20 rounded-xl transition-all duration-300 group">
                <Smile className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
              </button>
            </div>
            
            {/* Message Input */}
            <div className="flex-1 relative">
              <input
                type="text"
                className="w-full px-4 py-3 pr-12 bg-white/90 backdrop-blur-sm border border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-white/50 text-gray-800 placeholder-gray-500 text-sm sm:text-base shadow-lg"
                placeholder={`Message as ${activeUser.name}...`}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
              />
              <button className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 hover:bg-white/50 rounded-lg transition-colors">
                <Mic className="w-4 h-4 text-gray-600" />
              </button>
            </div>
            
            {/* Send Button */}
            <button
              onClick={handleSend}
              disabled={!newMessage.trim()}
              className="p-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl text-white shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95 transition-all duration-300 group"
            >
              <Send className="w-5 h-5 group-hover:rotate-12 transition-transform" />
            </button>
          </div>
          
          {/* Quick Reactions */}
          <div className="flex gap-2 mt-3 overflow-x-auto">
            {quickReactions.map((reaction, index) => (
              <button
                key={index}
                className="px-3 py-2 bg-white/80 backdrop-blur-sm rounded-full text-sm hover:bg-white hover:scale-110 transition-all duration-200 shadow-md"
                onClick={() => {
                  setNewMessage(prev => prev + reaction);
                }}
              >
                {reaction}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}